import { useState, useEffect } from 'react';
import { api } from '../api';
import { useAuth } from '../AuthContext';
import toast from 'react-hot-toast';
import { confirmDialog } from '../components/ConfirmDialog';

const ALL_PERMISSIONS = [
  { key: 'dashboard',  label: 'Dashboard' },
  { key: 'sales',      label: 'Sales' },
  { key: 'purchases',  label: 'Purchases' },
  { key: 'products',   label: 'Products' },
  { key: 'categories', label: 'Categories' },
  { key: 'suppliers',  label: 'Suppliers' },
  { key: 'reports',    label: 'Reports' },
  { key: 'users',      label: 'User Management' },
];

function PermissionBadges({ permissions, isAdmin }) {
  if (isAdmin) return <span className="badge badge-admin">All (Admin)</span>;
  const perms = typeof permissions === 'string'
    ? permissions.split(',').filter(Boolean)
    : (permissions || []);
  if (!perms.length) return <span className="badge badge-none">No access</span>;
  return (
    <div className="badge-list">
      {perms.map(p => {
        const found = ALL_PERMISSIONS.find(x => x.key === p);
        return <span key={p} className="badge badge-perm">{found?.label || p}</span>;
      })}
    </div>
  );
}

function UserModal({ user, onClose, onSave }) {
  const isEdit = !!user?.id;
  const [form, setForm] = useState({
    username:    user?.username    || '',
    displayName: user?.displayName || '',
    password:    '',
    isAdmin:     user?.isAdmin     ?? false,
    isActive:    user?.isActive    ?? true,
    permissions: user ? (
      typeof user.permissions === 'string'
        ? user.permissions.split(',').filter(Boolean)
        : (user.permissions || [])
    ) : ['dashboard'],
  });
  const [saving, setSaving] = useState(false);

  const togglePerm = (key) => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter(p => p !== key)
        : [...f.permissions, key]
    }));
  };

  const handle = async (e) => {
    e.preventDefault();
    if (!isEdit && !form.password) { toast.error('Password is required'); return; }
    setSaving(true);
    try {
      const payload = {
        displayName: form.displayName,
        isAdmin:     form.isAdmin,
        isActive:    form.isActive,
        permissions: form.permissions,
        ...(form.password ? { newPassword: form.password } : {}),
      };
      if (isEdit) {
        await onSave('update', user.id, payload);
      } else {
        await onSave('create', null, { username: form.username, password: form.password, ...payload });
      }
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? 'Edit User' : 'Add User'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handle} className="modal-body" style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div className="form-row" style={{ marginBottom: 0 }}>
            <div className="field">
              <label>Username</label>
              <input
                value={form.username}
                onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                disabled={isEdit}
                required={!isEdit}
                placeholder="jdoe"
              />
            </div>
            <div className="field">
              <label>Display Name</label>
              <input
                value={form.displayName}
                onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
                required
                placeholder="John Doe"
              />
            </div>
          </div>

          <div className="field">
            <label>{isEdit ? 'New Password (leave blank to keep)' : 'Password'}</label>
            <input
              type="password"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder="••••••••"
            />
          </div>

          <div style={{ display:'flex', gap:24 }}>
            <label className="toggle-row">
              <input type="checkbox" checked={form.isAdmin}
                onChange={e => setForm(f => ({ ...f, isAdmin: e.target.checked }))} />
              <span>Admin (full access)</span>
            </label>
            {isEdit && (
              <label className="toggle-row">
                <input type="checkbox" checked={form.isActive}
                  onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
                <span>Active</span>
              </label>
            )}
          </div>

          {!form.isAdmin && (
            <div>
              <label style={{ fontSize:12, color:'var(--muted)', marginBottom:8, display:'block' }}>
                PAGE PERMISSIONS
              </label>
              <div className="perm-grid">
                {ALL_PERMISSIONS.map(p => (
                  <label key={p.key} className={`perm-check ${form.permissions.includes(p.key) ? 'active' : ''}`}>
                    <input
                      type="checkbox"
                      checked={form.permissions.includes(p.key)}
                      onChange={() => togglePerm(p.key)}
                    />
                    {p.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create User')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers]   = useState([]);
  const [modal, setModal]   = useState(null); // null | 'add' | user-object
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { setUsers(await api.getUsers()); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (action, id, payload) => {
    if (action === 'create') {
      await api.createUser(payload);
      toast.success('User created');
    } else {
      await api.updateUser(id, payload);
      toast.success('User updated');
    }
    load();
  };

  const handleDelete = async (u) => {
    if (!(await confirmDialog(`Delete user "${u.username}"? This cannot be undone.`, { danger: true, confirmText: 'Delete' }))) return;
    try {
      await api.deleteUser(u.id);
      toast.success('User deleted');
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="page">
      <div className="page-header flex-between">
        <div>
          <h2>User Management</h2>
          <p className="page-sub">Control who can access which pages</p>
        </div>
        <button className="btn-primary" onClick={() => setModal('add')}>+ Add User</button>
      </div>

      {loading ? (
        <div className="empty">Loading users…</div>
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>User</th>
                <th>Username</th>
                <th>Status</th>
                <th>Permissions</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td data-label="User">
                    <div className="user-cell">
                      <div className="user-avatar">{(u.displayName || u.username)[0].toUpperCase()}</div>
                      <span>{u.displayName || u.username}</span>
                    </div>
                  </td>
                  <td style={{ fontFamily:'monospace', fontSize:13 }} data-label="Username">{u.username}</td>
                  <td data-label="Status">
                    <span className={`badge ${u.isActive ? 'badge-active' : 'badge-inactive'}`}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td data-label="Permissions">
                    <PermissionBadges permissions={u.permissions} isAdmin={u.isAdmin} />
                  </td>
                  <td data-label="Actions">
                    <div style={{ display:'flex', gap:6 }}>
                      <button className="btn-sm" onClick={() => setModal(u)}>Edit</button>
                      {u.id !== me?.id && (
                        <button className="btn-sm btn-danger" onClick={() => handleDelete(u)}>Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <UserModal
          user={modal === 'add' ? null : modal}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
